import { CALCULATOR_PAGE_KEYS } from './calculatorPageVisits';
import {
  buildDistricts2LockedCards,
  buildDistricts3LockedCards,
  buildEvents2LockedCards,
  buildEvents3LockedCards,
  buildFestivals2LockedCards,
  buildFestivals3LockedCards,
  buildFestivalsLockedCards,
  buildHotels2LockedCards,
  buildHotels3LockedCards,
  buildRestaurants2LockedCards,
  buildRestaurants3LockedCards,
  computeDistricts2Metrics,
  computeDistricts3Metrics,
  computeEvents2Metrics,
  computeEvents3Metrics,
  computeFestivals2Metrics,
  computeFestivals3Metrics,
  computeFestivalsMetrics,
  computeHotels2Metrics,
  computeHotels3Metrics,
  computeRestaurants2Metrics,
  computeRestaurants3Metrics,
} from './forkCalculatorMath';

/** @typedef {'number' | 'range'} ForkFieldType */

/**
 * @typedef {Object} ForkFieldConfig
 * @property {string} id
 * @property {string} label
 * @property {ForkFieldType} inputType
 * @property {string} defaultValue
 * @property {number} [min]
 * @property {number} [max]
 * @property {number} [step]
 * @property {string} [placeholder]
 */

/**
 * @typedef {Object} ForkCalculatorConfig
 * @property {string} pageKey
 * @property {string} leadSource
 * @property {string} leadCampaign
 * @property {string} calculatorType
 * @property {string} frictionZone
 * @property {string} eyebrow
 * @property {string} title
 * @property {string} subtitle
 * @property {{ value: string, label: string }[]} statStrip
 * @property {ForkFieldConfig[]} steps
 * @property {string} submitLabel
 * @property {(inputs: Record<string, string>) => object} computeMetrics
 * @property {(metrics: object, formatMoney: (n: number) => string) => object[]} buildLockedCards
 * @property {(metrics: object) => number} getTotalAmount
 * @property {string} totalBannerLabel
 * @property {string} totalBannerSubline
 * @property {string} heroHeadlinePrefix
 * @property {string} heroSubhead
 * @property {string} gateHeadline
 * @property {string} gateBody
 * @property {string[]} gateChecks
 * @property {string} gridAriaLabel
 * @property {(metrics: object) => { totalFans?: number, averageOrderValue?: number, percentNeverOrdered?: number }} [journeyMap]
 */

/** @type {Record<string, ForkCalculatorConfig>} */
export const FORK_CALCULATOR_REGISTRY = {
  'restaurants-2': {
    pageKey: CALCULATOR_PAGE_KEYS.RESTAURANTS2_CALCULATOR,
    leadSource: 'restaurants2_calculator',
    leadCampaign: 'restaurants2-reorder-calculator',
    calculatorType: 'restaurants2',
    frictionZone: 'Second-round reorder and wait friction',
    eyebrow: 'The reorder gap your floor team hasn\'t priced yet',
    title: 'Guests who get round two fast become regulars.',
    subtitle: 'Find out what slow reorders cost you on peak nights (takes less than 60 seconds)',
    statStrip: [
      { value: '35%', label: 'of guests skip a second round when the line doesn\'t move' },
      { value: '26', label: 'peak service nights most bars model per month' },
      { value: '55%', label: 'of a typical second-round check lost to reorder friction' },
    ],
    steps: [
      { id: 'peakCovers', label: 'How many covers do you serve on a peak night?', inputType: 'number', defaultValue: '180', min: 1, step: 1, placeholder: '180' },
      { id: 'avgSpend', label: 'What\'s your average spend per cover?', inputType: 'number', defaultValue: '42', min: 0.01, step: 0.01, placeholder: '42' },
      { id: 'reorderDropRate', label: 'What percent of guests skip a second round because of wait friction?', inputType: 'range', defaultValue: '35', min: 10, max: 80, step: 1 },
    ],
    submitLabel: 'Show Me What Reorder Friction Is Costing Me',
    computeMetrics: computeRestaurants2Metrics,
    buildLockedCards: buildRestaurants2LockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total annual reorder gap',
    totalBannerSubline: 'This is what second-round friction costs before lifetime value.',
    heroHeadlinePrefix: 'ANNUAL REORDER REVENUE AT RISK:',
    heroSubhead: 'Based on your peak covers and reorder drop-off rate',
    gateHeadline: 'Want the full reorder damage report?',
    gateBody: 'We\'ll show you per-shift leakage, monthly impact, and the one fix for peak service.',
    gateChecks: ['Full breakdown of all reorder gap metrics', '1 actionable fix for your busiest shifts'],
    gridAriaLabel: 'Reorder friction breakdown',
    journeyMap: (m) => ({ totalFans: m.peakCovers, averageOrderValue: m.avgSpend, percentNeverOrdered: Math.round(m.reorderDropRate) }),
  },
  'restaurants-3': {
    pageKey: CALCULATOR_PAGE_KEYS.RESTAURANTS3_CALCULATOR,
    leadSource: 'restaurants3_calculator',
    leadCampaign: 'restaurants3-retention-calculator',
    calculatorType: 'restaurants3',
    frictionZone: 'Repeat guest retention and return visits',
    eyebrow: 'The regulars gap your marketing team hasn\'t priced yet',
    title: 'Guests who feel recognized come back.',
    subtitle: 'Find out what lapsed regulars cost you each year (takes less than 60 seconds)',
    statStrip: [
      { value: '68%', label: 'is a common monthly return rate for neighborhood venues' },
      { value: '12', label: 'visits before a lapsed guest fully exits your revenue stream' },
      { value: '1%', label: 'return-rate drop can erase five figures across a mid-size venue' },
    ],
    steps: [
      { id: 'monthlyGuests', label: 'How many unique guests visit per month?', inputType: 'number', defaultValue: '2400', min: 1, step: 1, placeholder: '2400' },
      { id: 'avgSpend', label: 'What\'s your average spend per guest?', inputType: 'number', defaultValue: '38', min: 0.01, step: 0.01, placeholder: '38' },
      { id: 'returnRate', label: 'What percent of guests return within 30 days? (industry avg is 68%)', inputType: 'range', defaultValue: '68', min: 30, max: 95, step: 1 },
      { id: 'visitsBeforeChurn', label: 'How many visits does a regular make before they lapse?', inputType: 'number', defaultValue: '12', min: 1, step: 1, placeholder: '12' },
    ],
    submitLabel: 'Show Me What Lapsed Regulars Are Costing Me',
    computeMetrics: computeRestaurants3Metrics,
    buildLockedCards: buildRestaurants3LockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total annual retention gap',
    totalBannerSubline: 'This is what your return rate costs before lifetime value.',
    heroHeadlinePrefix: 'ANNUAL REVENUE AT RISK FROM LAPSED REGULARS:',
    heroSubhead: 'Based on your monthly guests and return rate',
    gateHeadline: 'Want the full retention damage report?',
    gateBody: 'We\'ll show lifetime value at risk, recovery scenarios, and the one fix before next month.',
    gateChecks: ['Full breakdown of all retention gaps', '1 actionable fix you can put in place this month'],
    gridAriaLabel: 'Repeat guest retention breakdown',
    journeyMap: (m) => ({ totalFans: m.monthlyGuests, averageOrderValue: m.avgSpend, percentNeverOrdered: Math.round(100 - m.returnRate) }),
  },
  'hotels-2': {
    pageKey: CALCULATOR_PAGE_KEYS.HOTELS2_CALCULATOR,
    leadSource: 'hotels2_calculator',
    leadCampaign: 'hotels2-roomservice-calculator',
    calculatorType: 'hotels2',
    frictionZone: 'In-room dining and room service capture',
    eyebrow: 'The room service gap your F&B team hasn\'t priced yet',
    title: 'Guests who can order in-room spend on-property.',
    subtitle: 'Find out what low room-service capture costs you (takes less than 60 seconds)',
    statStrip: [
      { value: '11%', label: 'of hotel guests order room service on average' },
      { value: '$38', label: 'average room service order value' },
      { value: '45%', label: 'of missed ancillary revenue recoverable with targeted offers' },
    ],
    steps: [
      { id: 'guestsPerNight', label: 'How many guests are on property on an average night?', inputType: 'number', defaultValue: '220', min: 1, step: 1, placeholder: '220' },
      { id: 'roomServiceRate', label: 'What percent of guests order room service? (industry avg is 11%)', inputType: 'range', defaultValue: '11', min: 2, max: 40, step: 1 },
      { id: 'avgOrder', label: 'What\'s your average room service order value?', inputType: 'number', defaultValue: '38', min: 0.01, step: 0.01, placeholder: '38' },
      { id: 'occupiedNights', label: 'How many occupied nights do you model per month?', inputType: 'number', defaultValue: '26', min: 1, step: 1, placeholder: '26' },
    ],
    submitLabel: 'Show Me What Room Service Gaps Are Costing Me',
    computeMetrics: computeHotels2Metrics,
    buildLockedCards: buildHotels2LockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total annual room service gap',
    totalBannerSubline: 'This is what below-benchmark capture costs your property.',
    heroHeadlinePrefix: 'ANNUAL ROOM SERVICE REVENUE AT RISK:',
    heroSubhead: 'Based on your capture rate vs. industry benchmark',
    gateHeadline: 'Want the full room service gap report?',
    gateBody: 'We\'ll show nightly leakage, monthly impact, and the one fix for on-property dining.',
    gateChecks: ['Full breakdown of in-room order gaps', '1 actionable fix for guest capture'],
    gridAriaLabel: 'Room service capture breakdown',
    journeyMap: (m) => ({ totalFans: m.guestsPerNight, averageOrderValue: m.avgOrder, percentNeverOrdered: Math.round(100 - m.roomServiceRate) }),
  },
  'hotels-3': {
    pageKey: CALCULATOR_PAGE_KEYS.HOTELS3_CALCULATOR,
    leadSource: 'hotels3_calculator',
    leadCampaign: 'hotels3-amenity-calculator',
    calculatorType: 'hotels3',
    frictionZone: 'Amenity and ancillary upsell capture',
    eyebrow: 'The ancillary gap your guest experience team hasn\'t priced yet',
    title: 'Guests who see the right offer book the amenity.',
    subtitle: 'Find out what missed upsells cost your property (takes less than 60 seconds)',
    statStrip: [
      { value: '19%', label: 'of hotel guests engage with paid amenities' },
      { value: '$24', label: 'average paid amenity spend per engaging guest' },
      { value: '45%', label: 'of missed ancillary revenue recoverable with targeted offers' },
    ],
    steps: [
      { id: 'guestsPerNight', label: 'How many guests are on property on an average night?', inputType: 'number', defaultValue: '220', min: 1, step: 1, placeholder: '220' },
      { id: 'amenityRate', label: 'What percent of guests use paid amenities? (industry avg is 19%)', inputType: 'range', defaultValue: '19', min: 5, max: 50, step: 1 },
      { id: 'avgAmenitySpend', label: 'What\'s your average paid amenity spend?', inputType: 'number', defaultValue: '24', min: 0.01, step: 0.01, placeholder: '24' },
      { id: 'occupiedNights', label: 'How many occupied nights do you model per month?', inputType: 'number', defaultValue: '26', min: 1, step: 1, placeholder: '26' },
    ],
    submitLabel: 'Show Me What Amenity Gaps Are Costing Me',
    computeMetrics: computeHotels3Metrics,
    buildLockedCards: buildHotels3LockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total annual amenity gap',
    totalBannerSubline: 'This is what below-benchmark ancillary capture costs annually.',
    heroHeadlinePrefix: 'ANNUAL ANCILLARY REVENUE AT RISK:',
    heroSubhead: 'Based on your amenity capture vs. industry benchmark',
    gateHeadline: 'Want the full ancillary gap report?',
    gateBody: 'We\'ll show nightly leakage, recovery scenarios, and the one fix for on-property upsells.',
    gateChecks: ['Full breakdown of amenity capture gaps', '1 actionable fix for ancillary revenue'],
    gridAriaLabel: 'Amenity upsell breakdown',
    journeyMap: (m) => ({ totalFans: m.guestsPerNight, averageOrderValue: m.avgAmenitySpend, percentNeverOrdered: Math.round(100 - m.amenityRate) }),
  },
  'districts-2': {
    pageKey: CALCULATOR_PAGE_KEYS.DISTRICTS2_CALCULATOR,
    leadSource: 'districts2_calculator',
    leadCampaign: 'districts2-lift-calculator',
    calculatorType: 'districts2',
    frictionZone: 'Member business coordinated ordering lift',
    eyebrow: 'The corridor lift gap your tenants haven\'t modeled together',
    title: 'Districts that coordinate ordering capture more per visit.',
    subtitle: 'Find out what lift you\'re leaving across member businesses (takes less than 60 seconds)',
    statStrip: [
      { value: '23%', label: 'gross sales lift when mobile ordering is available' },
      { value: '104', label: 'weekend days modeled across the year' },
      { value: '60%', label: 'conservative discount applied to corridor projections' },
    ],
    steps: [
      { id: 'memberBusinesses', label: 'How many member businesses are in your district?', inputType: 'number', defaultValue: '24', min: 1, step: 1, placeholder: '24' },
      { id: 'friSatFootTraffic', label: 'Average Friday + Saturday foot traffic per weekend?', inputType: 'number', defaultValue: '3200', min: 1, step: 1, placeholder: '3200' },
      { id: 'currentLiftPct', label: 'What lift are you seeing from mobile ordering today? (benchmark is 23%)', inputType: 'range', defaultValue: '8', min: 0, max: 23, step: 1 },
    ],
    submitLabel: 'Show Me What Lift We\'re Leaving on the Table',
    computeMetrics: computeDistricts2Metrics,
    buildLockedCards: buildDistricts2LockedCards,
    getTotalAmount: (m) => m.corridor_gap,
    totalBannerLabel: 'Total corridor lift gap',
    totalBannerSubline: 'This is what coordinated ordering could add across member businesses.',
    heroHeadlinePrefix: 'ANNUAL CORRIDOR LIFT GAP:',
    heroSubhead: 'Based on your foot traffic and current lift vs. benchmark',
    gateHeadline: 'Want the full corridor lift report?',
    gateBody: 'We\'ll show per-weekend upside, member business impact, and the one fix for the district.',
    gateChecks: ['Full breakdown of corridor lift metrics', '1 actionable fix for member business coordination'],
    gridAriaLabel: 'District lift gap breakdown',
    journeyMap: (m) => ({ totalFans: m.friSatFootTraffic, averageOrderValue: 34, percentNeverOrdered: Math.round(23 - m.currentLiftPct) }),
  },
  'districts-3': {
    pageKey: CALCULATOR_PAGE_KEYS.DISTRICTS3_CALCULATOR,
    leadSource: 'districts3_calculator',
    leadCampaign: 'districts3-programming-calculator',
    calculatorType: 'districts3',
    frictionZone: 'Anchor programming and event-day capture',
    eyebrow: 'The programming ROI gap your events calendar hasn\'t priced yet',
    title: 'Anchor events should lift the whole corridor.',
    subtitle: 'Find out what uneven capture costs on programming weekends (takes less than 60 seconds)',
    statStrip: [
      { value: '72%', label: 'target capture rate on programmed weekends' },
      { value: '$34', label: 'average spend per visitor modeled' },
      { value: '2–3x', label: 'foot traffic spike on anchor programming weekends' },
    ],
    steps: [
      { id: 'programmingWeekends', label: 'How many anchor programming weekends do you run per year?', inputType: 'number', defaultValue: '18', min: 1, step: 1, placeholder: '18' },
      { id: 'avgAttendance', label: 'Average attendance on a programming weekend?', inputType: 'number', defaultValue: '4800', min: 1, step: 1, placeholder: '4800' },
      { id: 'spendPerHead', label: 'Average spend per visitor on event days?', inputType: 'number', defaultValue: '34', min: 0.01, step: 0.01, placeholder: '34' },
      { id: 'captureRate', label: 'What percent of visitors convert to on-site spend? (target is 72%)', inputType: 'range', defaultValue: '54', min: 20, max: 90, step: 1 },
    ],
    submitLabel: 'Show Me What Programming Gaps Are Costing Us',
    computeMetrics: computeDistricts3Metrics,
    buildLockedCards: buildDistricts3LockedCards,
    getTotalAmount: (m) => m.annual_gap,
    totalBannerLabel: 'Total programming capture gap',
    totalBannerSubline: 'This is what anchor events leave behind when capture lags.',
    heroHeadlinePrefix: 'ANNUAL PROGRAMMING ROI GAP:',
    heroSubhead: 'Based on your event attendance and capture rate',
    gateHeadline: 'Want the full programming ROI report?',
    gateBody: 'We\'ll show per-event leakage, annual impact, and the one fix for programming weekends.',
    gateChecks: ['Full breakdown of programming capture gaps', '1 actionable fix for anchor event days'],
    gridAriaLabel: 'Programming ROI breakdown',
    journeyMap: (m) => ({ totalFans: m.avgAttendance, averageOrderValue: m.spendPerHead, percentNeverOrdered: Math.round(100 - m.captureRate) }),
  },
  'events-2': {
    pageKey: CALCULATOR_PAGE_KEYS.EVENTS2_CALCULATOR,
    leadSource: 'events2_calculator',
    leadCampaign: 'events2-vip-calculator',
    calculatorType: 'events2',
    frictionZone: 'VIP recognition and guest handoff',
    eyebrow: 'The VIP gap your events team hasn\'t priced yet',
    title: 'VIPs who feel recognized rebook.',
    subtitle: 'Find out what unrecognized VIPs cost your venue (takes less than 60 seconds)',
    statStrip: [
      { value: '18%', label: 'of event fee value at risk when a VIP goes unrecognized' },
      { value: '15%', label: 'typical share of events with VIP exposure' },
      { value: '60%', label: 'of planner revenue driven by referrals and repeats' },
    ],
    steps: [
      { id: 'eventsPerYear', label: 'How many events do you host per year?', inputType: 'number', defaultValue: '120', min: 1, step: 1, placeholder: '120' },
      { id: 'avgEventFee', label: 'What\'s your average event fee?', inputType: 'number', defaultValue: '8500', min: 0.01, step: 0.01, placeholder: '8500' },
      { id: 'vipSharePct', label: 'What percent of events include VIP guests?', inputType: 'range', defaultValue: '15', min: 5, max: 60, step: 1 },
      { id: 'vipMissRate', label: 'What percent of VIP moments go unrecognized? (industry avg is 18%)', inputType: 'range', defaultValue: '18', min: 5, max: 50, step: 1 },
    ],
    submitLabel: 'Show Me What VIP Gaps Are Costing Me',
    computeMetrics: computeEvents2Metrics,
    buildLockedCards: buildEvents2LockedCards,
    getTotalAmount: (m) => m.annual_gap,
    totalBannerLabel: 'Total VIP recognition gap',
    totalBannerSubline: 'This is what handoff failures cost in fees and referrals.',
    heroHeadlinePrefix: 'ANNUAL VIP RECOGNITION GAP:',
    heroSubhead: 'Based on your VIP exposure and miss rate',
    gateHeadline: 'Want the full VIP gap report?',
    gateBody: 'We\'ll show fee risk, referral exposure, and the one fix before your next event.',
    gateChecks: ['Full breakdown of VIP recognition gaps', '1 actionable fix for guest handoffs'],
    gridAriaLabel: 'VIP recognition breakdown',
    journeyMap: (m) => ({ totalFans: m.eventsPerYear, averageOrderValue: m.avgEventFee, percentNeverOrdered: Math.round(m.vipMissRate) }),
  },
  'events-3': {
    pageKey: CALCULATOR_PAGE_KEYS.EVENTS3_CALCULATOR,
    leadSource: 'events3_calculator',
    leadCampaign: 'events3-referral-calculator',
    calculatorType: 'events3',
    frictionZone: 'Planner referral and repeat booking pipeline',
    eyebrow: 'The referral gap your sales team hasn\'t priced yet',
    title: 'Planners who trust your handoff send the next booking.',
    subtitle: 'Find out what referral leakage costs your venue (takes less than 60 seconds)',
    statStrip: [
      { value: '60%', label: 'of planner revenue typically driven by referrals and repeats' },
      { value: '45%', label: 'of guest preferences lost with verbal day-of handoffs' },
      { value: '3 yrs', label: 'average planner relationship window modeled' },
    ],
    steps: [
      { id: 'eventsPerYear', label: 'How many events do you host per year?', inputType: 'number', defaultValue: '120', min: 1, step: 1, placeholder: '120' },
      { id: 'avgEventFee', label: 'What\'s your average event fee?', inputType: 'number', defaultValue: '8500', min: 0.01, step: 0.01, placeholder: '8500' },
      { id: 'referralRate', label: 'What percent of revenue comes from referrals and repeats?', inputType: 'range', defaultValue: '60', min: 20, max: 90, step: 1 },
      { id: 'referralDropPct', label: 'What percent of referral pipeline is at risk from weak follow-up?', inputType: 'range', defaultValue: '22', min: 5, max: 60, step: 1 },
    ],
    submitLabel: 'Show Me What Referral Leakage Is Costing Me',
    computeMetrics: computeEvents3Metrics,
    buildLockedCards: buildEvents3LockedCards,
    getTotalAmount: (m) => m.annual_gap,
    totalBannerLabel: 'Total referral pipeline gap',
    totalBannerSubline: 'This is what weak follow-up costs in repeat bookings.',
    heroHeadlinePrefix: 'ANNUAL REFERRAL PIPELINE GAP:',
    heroSubhead: 'Based on your referral dependency and follow-up leakage',
    gateHeadline: 'Want the full referral gap report?',
    gateBody: 'We\'ll show pipeline risk, multi-year impact, and the one fix for planner retention.',
    gateChecks: ['Full breakdown of referral pipeline gaps', '1 actionable fix for post-event follow-up'],
    gridAriaLabel: 'Planner referral breakdown',
    journeyMap: (m) => ({ totalFans: m.eventsPerYear, averageOrderValue: m.avgEventFee, percentNeverOrdered: Math.round(m.referralDropPct) }),
  },
  festivals: {
    pageKey: CALCULATOR_PAGE_KEYS.FESTIVALS_CALCULATOR,
    leadSource: 'festivals_calculator',
    leadCampaign: 'festivals-queue-calculator',
    calculatorType: 'festivals',
    frictionZone: 'Vendor queue abandonment and walk-aways',
    eyebrow: 'The queue gap your festival ops team hasn\'t priced yet',
    title: 'Attendees who skip the line take their spend with them.',
    subtitle: 'Find out what vendor walk-aways cost your event (takes less than 60 seconds)',
    statStrip: [
      { value: '23%', label: 'gross sales lift when mobile ordering is available' },
      { value: '28%', label: 'typical walk-away rate when vendor lines stall' },
      { value: '$28', label: 'average vendor spend per converting attendee' },
    ],
    steps: [
      { id: 'dailyAttendance', label: 'How many attendees on a typical festival day?', inputType: 'number', defaultValue: '12000', min: 1, step: 1, placeholder: '12000' },
      { id: 'vendorStalls', label: 'How many food and beverage vendors are on site?', inputType: 'number', defaultValue: '45', min: 1, step: 1, placeholder: '45' },
      { id: 'walkawayRate', label: 'What percent of attendees abandon vendor lines? (industry avg is 28%)', inputType: 'range', defaultValue: '28', min: 10, max: 60, step: 1 },
    ],
    submitLabel: 'Show Me What Queue Walk-Aways Are Costing Me',
    computeMetrics: computeFestivalsMetrics,
    buildLockedCards: buildFestivalsLockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total vendor queue gap',
    totalBannerSubline: 'This is what line abandonment costs across your event days.',
    heroHeadlinePrefix: 'ANNUAL VENDOR QUEUE GAP:',
    heroSubhead: 'Based on your attendance and walk-away rate',
    gateHeadline: 'Want the full queue gap report?',
    gateBody: 'We\'ll show daily leakage, seasonal impact, and the one fix for vendor friction.',
    gateChecks: ['Full breakdown of queue abandonment metrics', '1 actionable fix for vendor lines'],
    gridAriaLabel: 'Vendor queue breakdown',
    journeyMap: (m) => ({ totalFans: m.dailyAttendance, averageOrderValue: 28, percentNeverOrdered: Math.round(m.walkawayRate) }),
  },
  'festivals-2': {
    pageKey: CALCULATOR_PAGE_KEYS.FESTIVALS2_CALCULATOR,
    leadSource: 'festivals2_calculator',
    leadCampaign: 'festivals2-merch-calculator',
    calculatorType: 'festivals2',
    frictionZone: 'Merch impulse purchase friction',
    eyebrow: 'The merch gap your festival retail team hasn\'t priced yet',
    title: 'Impulse merch dies in line.',
    subtitle: 'Find out what queue friction costs in merch sales (takes less than 60 seconds)',
    statStrip: [
      { value: '15–25%', label: 'per-head merch lift with near-zero friction checkout' },
      { value: '22%', label: 'typical merch conversion at outdoor events' },
      { value: '$35', label: 'average festival merch order value' },
    ],
    steps: [
      { id: 'dailyAttendance', label: 'How many attendees on a typical festival day?', inputType: 'number', defaultValue: '12000', min: 1, step: 1, placeholder: '12000' },
      { id: 'merchConversion', label: 'What percent of attendees buy merch? (industry avg is 22%)', inputType: 'range', defaultValue: '14', min: 5, max: 40, step: 1 },
      { id: 'avgMerchOrder', label: 'What\'s your average merch order value?', inputType: 'number', defaultValue: '35', min: 0.01, step: 0.01, placeholder: '35' },
    ],
    submitLabel: 'Show Me What Merch Impulse Loss Is Costing Me',
    computeMetrics: computeFestivals2Metrics,
    buildLockedCards: buildFestivals2LockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total merch impulse gap',
    totalBannerSubline: 'This is what line friction costs in impulse purchases.',
    heroHeadlinePrefix: 'ANNUAL MERCH IMPULSE GAP:',
    heroSubhead: 'Based on your merch conversion vs. industry benchmark',
    gateHeadline: 'Want the full merch gap report?',
    gateBody: 'We\'ll show daily impulse loss, seasonal impact, and the one fix for merch booths.',
    gateChecks: ['Full breakdown of merch impulse metrics', '1 actionable fix for merch lines'],
    gridAriaLabel: 'Merch impulse breakdown',
    journeyMap: (m) => ({ totalFans: m.dailyAttendance, averageOrderValue: m.avgMerchOrder, percentNeverOrdered: Math.round(22 - m.merchConversion) }),
  },
  'festivals-3': {
    pageKey: CALCULATOR_PAGE_KEYS.FESTIVALS3_CALCULATOR,
    leadSource: 'festivals3_calculator',
    leadCampaign: 'festivals3-perhead-calculator',
    calculatorType: 'festivals3',
    frictionZone: 'Per-attendee spend capture gap',
    eyebrow: 'The per-head gap your festival revenue team hasn\'t priced yet',
    title: 'Sold-out gates don\'t mean captured spend.',
    subtitle: 'Find out what per-head leakage costs across your run (takes less than 60 seconds)',
    statStrip: [
      { value: '$52', label: 'target per-head spend at premium outdoor events' },
      { value: '$38', label: 'common actual capture when friction is high' },
      { value: '12', label: 'event days modeled across a typical season' },
    ],
    steps: [
      { id: 'dailyAttendance', label: 'How many attendees on a typical festival day?', inputType: 'number', defaultValue: '12000', min: 1, step: 1, placeholder: '12000' },
      { id: 'targetSpend', label: 'What\'s your target spend per attendee?', inputType: 'number', defaultValue: '52', min: 0.01, step: 0.01, placeholder: '52' },
      { id: 'actualSpend', label: 'What\'s your actual average spend per attendee today?', inputType: 'number', defaultValue: '38', min: 0.01, step: 0.01, placeholder: '38' },
      { id: 'eventDays', label: 'How many event days in your season?', inputType: 'number', defaultValue: '12', min: 1, step: 1, placeholder: '12' },
    ],
    submitLabel: 'Show Me What Per-Head Gaps Are Costing Me',
    computeMetrics: computeFestivals3Metrics,
    buildLockedCards: buildFestivals3LockedCards,
    getTotalAmount: (m) => m.annual_loss,
    totalBannerLabel: 'Total per-head capture gap',
    totalBannerSubline: 'This is what attendance without conversion costs your season.',
    heroHeadlinePrefix: 'SEASONAL PER-HEAD CAPTURE GAP:',
    heroSubhead: 'Based on your target vs. actual spend per attendee',
    gateHeadline: 'Want the full per-head gap report?',
    gateBody: 'We\'ll show daily leakage, full-season impact, and the one fix for capture rate.',
    gateChecks: ['Full breakdown of per-head capture metrics', '1 actionable fix for attendee spend'],
    gridAriaLabel: 'Per-head spend breakdown',
    journeyMap: (m) => ({ totalFans: m.dailyAttendance, averageOrderValue: m.actualSpend, percentNeverOrdered: Math.round((m.gapPerHead / m.targetSpend) * 100) }),
  },
};

export function getForkCalculatorConfig(configId) {
  return FORK_CALCULATOR_REGISTRY[configId] ?? null;
}

export const FORK_CALCULATOR_SLUGS = Object.keys(FORK_CALCULATOR_REGISTRY);
